'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import type { Category } from '@/types';

const t = {
  en: { all: 'All', label: 'Filter by category' },
  ar: { all: 'الكل', label: 'تصفية حسب التصنيف' },
} as const;

interface CategoryChipsProps {
  locale: string;
  /** URL section the chips point into: 'projects' or 'blog'. */
  section: 'projects' | 'blog';
  categories: Category[];
  /** Slug (English) of the selected category; undefined = "All". */
  activeSlug?: string;
  /** Total items across all categories, shown on the "All" chip. */
  total?: number;
  className?: string;
}

const base =
  'inline-flex shrink-0 snap-start items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-180';
const idle = 'border-(--border) bg-(--surface) text-(--muted) hover:border-primary-500/50 hover:text-(--text)';
const active = 'border-primary-500 bg-primary-500 text-neutral-900';
const countIdle = 'bg-(--border) text-(--muted)';
const countActive = 'bg-neutral-900/15 text-neutral-900';

/**
 * Category filter. Each chip is a real link to a server-rendered category page, so the filter is
 * crawlable, shareable and works with the back button (it is not client-side state). On phones it
 * is a swipeable single row with faded edges whose active chip is scrolled into view; from `sm` up
 * it wraps. Categories with no items are hidden, so a chip never leads to an empty page.
 */
export default function CategoryChips({ locale, section, categories, activeSlug, total, className = '' }: CategoryChipsProps) {
  const lang = locale === 'ar' ? 'ar' : 'en';
  const labels = t[lang];
  const activeRef = useRef<HTMLAnchorElement>(null);

  // Bring the selected chip into view on narrow screens (the row scrolls horizontally there).
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [activeSlug]);

  const visible = categories.filter((c) => c.count > 0 || c.slug.en === activeSlug);
  if (visible.length === 0) return null;
  const sum = total ?? visible.reduce((n, c) => n + c.count, 0);
  const isAll = !activeSlug;

  return (
    <nav aria-label={labels.label} className={`px-4 sm:px-6 pb-5 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <ul
          className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                     max-sm:mask-[linear-gradient(to_right,transparent,#000_20px,#000_calc(100%-20px),transparent)]
                     sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
        >
          <li className="shrink-0">
            <Link
              href={`/${locale}/${section}`}
              scroll={false}
              aria-current={isAll ? 'page' : undefined}
              ref={isAll ? activeRef : undefined}
              className={`${base} ${isAll ? active : idle}`}
            >
              {labels.all}
              <span className={`rounded-full px-1.5 text-xs tabular-nums ${isAll ? countActive : countIdle}`}>{sum}</span>
            </Link>
          </li>
          {visible.map((c) => {
            const isActive = c.slug.en === activeSlug;
            const slug = lang === 'ar' ? c.slug.ar || c.slug.en : c.slug.en;
            return (
              <li key={c._id} className="shrink-0">
                <Link
                  href={`/${locale}/${section}/category/${encodeURIComponent(slug)}`}
                  scroll={false}
                  aria-current={isActive ? 'page' : undefined}
                  ref={isActive ? activeRef : undefined}
                  className={`${base} ${isActive ? active : idle}`}
                >
                  {c.name[lang] || c.name.en}
                  <span className={`rounded-full px-1.5 text-xs tabular-nums ${isActive ? countActive : countIdle}`}>{c.count}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
